import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Button, Col } from "react-bootstrap";
import queryString from "query-string";
import { Link } from "react-router-dom";

import { deepEqual } from "../../utils/object";
import { currency as formatCurrency } from "../../utils/number";
import Table from "../../layout/table";
import CsLineIcons from "../../cs-line-icons";
import {
    checkout,
    checkoutStatus,
    mine as myProducts
} from "../../actions/products";
import { list as getCodes } from "../../actions/code";

import LicenseModal from "../product/upgrades";
import "./dashboard.css";

export const PRODUCT_ORDER_QUEUE_KEY = "feradel_product_order_queue";

const PRODUCT_PRESENTATION = {
    sign: { icon: "edit-square", tone: "sky", description: "Send documents for signature and manage electronic agreements." },
    email: { icon: "at-sign", tone: "violet", description: "Professional email tools for your business." },
    chat: { icon: "online-class", tone: "green", description: "Connect team conversations and keep work moving." },
    matters: { icon: "board-2", tone: "blue", description: "Complete matter and client management." },
    cal: { icon: "notebook-1", tone: "orange", description: "Smart calendar and scheduling." },
    calendar: { icon: "notebook-1", tone: "orange", description: "Smart calendar and scheduling." },
    survey: { icon: "chart-4", tone: "purple", description: "Create surveys and collect feedback." },
    files: { icon: "file-data", tone: "gold", description: "Secure file storage and sharing." },
    render: { icon: "file-text", tone: "rose", description: "Generate documents and PDFs." }
};

export const productPresentation = (row = {}) => {
    const name = String(row["Product.Code.description"] || row["Product.caption"] || row.key || "Feradel Product");
    const key = Object.keys(PRODUCT_PRESENTATION).find(item => name.toLowerCase().includes(item));
    return {
        name,
        ...(PRODUCT_PRESENTATION[key] || {
            icon: "grid-2",
            tone: "blue",
            description: row["Product.description"] || "Manage this licensed Feradel product."
        }),
        description: row["Product.description"] || PRODUCT_PRESENTATION[key]?.description || "Manage this licensed Feradel product."
    };
};

export const lifecyclePresentation = (value = {}) => {
    const status = value?.status || "Untracked";
    if (status === "Active") return { label: "Ready", detail: "All set and active", tone: "ready", icon: "check-circle" };
    if (["Provisioning", "Untracked"].includes(status)) return { label: "Setting up", detail: "Complete setup to get started", tone: "setup", icon: "sync-horizontal" };
    if (["DeletionPending", "Deprovisioning"].includes(status)) return { label: "Removal in progress", detail: "Access is being removed", tone: "warning", icon: "warning-hexagon" };
    if (status.endsWith("Failed")) return { label: "Needs attention", detail: "Open the product to retry", tone: "danger", icon: "error-hexagon" };
    return { label: status, detail: "Open for details", tone: "neutral", icon: "info-hexagon" };
};

export const activeSincePresentation = (value) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return { date: "Not available", time: "" };
    return {
        date: new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date),
        time: new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(date)
    };
};

export class Summary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            page: 0,
            size: 25,
            search: "",
            order: "Product.Code.description",
            index: 0,
            direction: "ASC",
            data: [],
            pages: 0,
            products: [],
            updated: null,
            checkout: {
                starting: false,
                message: "",
                variant: "info"
            },
            modal: {
                license: {
                    category: null
                }
            }
        };
    }

    

    componentDidUpdate = (old) => {
        if (!deepEqual(old?.matter, this.props?.matter)) {
            this.fetchData();
        } else if (!deepEqual(old?.updated, this.props?.updated)) {
            this.fetchData();
        }
    }

    componentDidMount = () => {
        this.fetchData();
        this.resumeCheckout();
    }

    fetchData = async () => {
        setTimeout(async () => {
            const __data = await this.props.myProducts({ page: this.state.page, size: this.state.size, order: this.state.order, direction: this.state.direction, raw: 1, search: this.state.search }) || [];
            const __products = await this.props.getCodes("PR") || [];
            let __count = 0;
            if (Array.isArray(__data) && __data.length > 0)
                __count = Math.ceil(Number(__data[0].count) / Number(this.state.size));

            const __state = {
                ...this.state,
                data: __data,
                pages: __count,
                products: [...__products]
            }
            
            this.applyRequestedLicenseWorkflow(__state);
            
            this.setState({
                ...__state
            });

        }, 30);
    }

    get action() {
        const queryParams = queryString.parse(window.location.search);
        return queryParams["action"] || "";
    }

    get category() {
        const queryParams = queryString.parse(window.location.search);
        return this.state?.modal?.license?.category || queryParams["category"] || "";
    }

    get plan() {
        const queryParams = queryString.parse(window.location.search);
        return queryParams["plan"] || "";
    }

    get checkoutResult() {
        const queryParams = queryString.parse(window.location.search);
        return queryParams["checkout"] || "";
    }

    get checkoutSessionId() {
        const queryParams = queryString.parse(window.location.search);
        return queryParams["session_id"] || "";
    }

    get removalResult() {
        const queryParams = queryString.parse(window.location.search);
        return queryParams["removal"] || "";
    }

    applyRequestedLicenseWorkflow = (state) => {
        if (this.action === "subscribe" && !this.plan && state.modal.license.hidden !== true) {
            state.modal.license.show = true;
        }
        return state;
    }

    waitForCheckoutPoll = () => new Promise(resolve => setTimeout(resolve, 1500));

    redirectToCheckout = (url) => window.location.assign(url);

    readProductOrderQueue = () => {
        try {
            const queue = JSON.parse(window.sessionStorage.getItem(PRODUCT_ORDER_QUEUE_KEY) || "[]");
            return Array.isArray(queue) ? queue : [];
        } catch (_error) {
            return [];
        }
    }

    writeProductOrderQueue = (queue) => {
        if (!Array.isArray(queue) || queue.length === 0) {
            window.sessionStorage.removeItem(PRODUCT_ORDER_QUEUE_KEY);
            return;
        }
        window.sessionStorage.setItem(PRODUCT_ORDER_QUEUE_KEY, JSON.stringify(queue));
    }

    resumeCheckout = async () => {
        if (this.action === "subscribe" && this.plan) {
            await this.startCheckout({ code: this.plan });
            return;
        }

        if (this.checkoutResult === "canceled") {
            this.writeProductOrderQueue([]);
            this.setState({
                checkout: {
                    starting: false,
                    message: "Checkout was canceled. No license was created and no new charge was made.",
                    variant: "warning"
                }
            });
            return;
        }

        if (this.removalResult === "started") {
            this.setState({
                checkout: {
                    starting: false,
                    message: "Product access has been revoked. Secure removal and retention processing are underway.",
                    variant: "info"
                }
            });
            return;
        }

        if (this.checkoutResult === "success" && this.checkoutSessionId) {
            this.setState({
                checkout: {
                    starting: true,
                    message: "Payment received. Confirming and provisioning your license…",
                    variant: "info"
                }
            });

            for (let attempt = 0; attempt < 8; attempt++) {
                const result = await this.props.checkoutStatus({ sessionId: this.checkoutSessionId });
                if (result?.order?.status === "complete") {
                    const queue = this.readProductOrderQueue();
                    if (queue.length > 0) {
                        this.setState(previous => ({
                            checkout: { ...previous.checkout, starting: false }
                        }));
                        const continued = await this.processProductOrderQueue(queue, true);
                        if (continued?.redirected || continued?.success === false) return;
                    }
                    this.setState({
                        updated: new Date(),
                        checkout: {
                            starting: false,
                            message: "Your payment is confirmed and your license is ready.",
                            variant: "success"
                        }
                    }, this.fetchData);
                    return;
                }
                if (result?.order?.status === "error" || result?.success === false) {
                    this.setState({
                        checkout: {
                            starting: false,
                            message: result?.error?.message || result?.order?.error || "License provisioning needs attention.",
                            variant: "danger"
                        }
                    });
                    return;
                }
                await this.waitForCheckoutPoll();
            }

            this.setState({
                checkout: {
                    starting: false,
                    message: "Payment is confirmed. Provisioning is still running; refresh this page in a moment.",
                    variant: "info"
                }
            });
        }
    }

    startCheckout = async ({ productUuid, code, noPayment = false }, allowWhileStarting = false) => {
        if (this.state.checkout.starting && !allowWhileStarting) return { success: false, busy: true };
        const activatingFreePlan = Boolean(
            noPayment || String(code || "").toUpperCase().endsWith(".FREE")
        );
        this.setState({
            checkout: {
                starting: true,
                message: activatingFreePlan ? "Activating your free license…" : "Preparing secure checkout…",
                variant: "info"
            }
        });

        const result = await this.props.checkout({ productUuid, code });
        if (result?.url) {
            this.redirectToCheckout(result.url);
            return { success: true, redirected: true, result };
        }
        if (result?.success && result?.order?.status === "complete") {
            window.history.replaceState({}, "", "/dashboard?checkout=free");
            this.setState({
                updated: new Date(),
                checkout: {
                    starting: false,
                    message: result.reused
                        ? "This free plan is already active on your account."
                        : "Your free license is ready.",
                    variant: "success"
                }
            }, this.fetchData);
            return { success: true, complete: true, result };
        }

        this.setState({
            checkout: {
                starting: false,
                message: result?.error?.message || "Unable to prepare checkout.",
                variant: "danger"
            }
        });
        return { success: false, error: result?.error || { message: "Unable to prepare checkout." } };
    }

    processProductOrderQueue = async (selections, resuming = false) => {
        const queue = [...(selections || [])];
        while (queue.length > 0) {
            const selection = queue.shift();
            this.writeProductOrderQueue(queue);
            const result = await this.startCheckout({
                productUuid: selection.planId,
                noPayment: Boolean(selection.noPayment)
            }, true);
            if (result?.redirected) return result;
            if (result?.success === false) {
                this.writeProductOrderQueue([selection, ...queue]);
                return result;
            }
        }

        this.writeProductOrderQueue([]);
        if (!resuming) {
            this.setState(previous => ({
                checkout: {
                    ...previous.checkout,
                    starting: false,
                    message: "Your product licenses are ready.",
                    variant: "success"
                }
            }), this.fetchData);
        }
        return { success: true, complete: true };
    }

    get headers() {
        return [
            {
                header: "Product",
                sortable: true,
                source: "Product.Code.description",
                className: "product-column",
                dataClassName: "product-cell",
                onRender: this.onProductFormat
            },
            {
                header: "Status",
                sortable: false,
                source: "lifecycle",
                className: "status-column",
                dataClassName: "status-cell",
                onRender: this.onLifecycleFormat
            },
            {
                header: "Plan",
                sortable: false,
                source: "frequency",
                className: "plan-column",
                dataClassName: "plan-cell",
                onRender: this.onPlanFormat
            },
            {
                header: "Price",
                sortable: true,
                source: "price",
                className: "price-column",
                dataClassName: "price-cell",
                onRender: this.onPriceFormat
            },
            {
                header: "Active Since",
                sortable: true,
                source: "startDate",
                className: "active-since-column",
                dataClassName: "active-since-cell",
                onRender: this.onActiveSinceFormat
            }
        ]
    }

    formatFrequencyCode = (e) => {
        switch (e) {
            case "P":
                return "Forever";
            case "M":
                return "Monthly";
            case "A":
                return "Annually";
            default:
                return "Unknown";
        }
    }

    onPriceFormat = (e, row) => {
        const recurring = ["M", "A"].includes(String(row.frequency));
        return (
            <span className="product-price">
                <strong>{formatCurrency(Number(e) || 0)}</strong>
                <small>{recurring ? `per ${String(row.frequency) === "A" ? "year" : "month"}` : "one-time"}</small>
            </span>
        );
    }

    onPlanFormat = (_value, row) => {
        const priceLabel = Number(row.price) === 0 ? "Free" : (row["Product.caption"] || "Licensed");
        return <span className="product-plan">{priceLabel} {this.formatFrequencyCode(String(row.frequency || ""))}</span>;
    }

    onLifecycleFormat = (value = {}) => {
        const presentation = lifecyclePresentation(value);
        return (
            <span className={`product-status is-${presentation.tone}`}>
                <span className="product-status-title"><CsLineIcons icon={presentation.icon} size="15" />{presentation.label}</span>
                <small>{presentation.detail}</small>
            </span>
        );
    }

    onProductFormat = (_value, row) => {
        const presentation = productPresentation(row);
        return (
            <Link className="product-identity" to={`/dashboard/products/${row.uuid.toString()}`}>
                <span className={`product-icon is-${presentation.tone}`}><CsLineIcons icon={presentation.icon} size="22" /></span>
                <span>
                    <strong>{presentation.name}</strong>
                    <small>{presentation.description}</small>
                </span>
            </Link>
        );
    }

    onActiveSinceFormat = (value) => {
        const presentation = activeSincePresentation(value);
        return (
            <span className="product-active-since">
                <span><CsLineIcons icon="notebook-1" size="14" />{presentation.date}</span>
                {presentation.time ? <small>{presentation.time}</small> : null}
            </span>
        );
    }

    onColumnClick = (e) => {
        const __array = this.headers;
        const __index = __array.findIndex((item) => {
            return item.header === e.header && item.source === e.source;
        });

        this.setState({
            ...this.state,
            index: __index,
            direction: this.state.index === __index && this.state.direction === "DESC" ? "ASC" : "DESC",
            order: __array[__index].source,
            page: 0
        }, this.fetchData);
    }

    onSearch = (e) => {
        this.setState({
            ...this.state,
            search: e
        }, this.fetchData);
    }

    onSize = (e) => {
        this.setState({
            ...this.state,
            page: 0,
            size: Number(e)
        }, this.fetchData);
    }

    onPageChange = (e) => {
        this.setState({
            ...this.state,
            page: Number(e)
        }, this.fetchData);
    }

    onLicenseClick = async (e) => {
        this.setState({
            ...this.state,
            modal: {
                ...this.state.modal,
                license: {
                    ...this.state.modal.license,
                    category: null,
                    show: true
                }
            }
        })
    }
    
    onLicenseHideModal = async (e) => {
        this.setState({
            ...this.state,
            modal: {
                ...this.state.modal,
                license: {
                    ...this.state.modal.license,
                    show: false,
                    hidden: true
                }
            }
        })
    }
    
    onLicenseOk = async ({ selections = [], estimate = null, uuid = null }) => {
        const estimatedLines = new Map(
            (estimate?.lineItems || []).map(line => [line.planId, line])
        );
        const queue = selections.length > 0
            ? selections.map(selection => {
                const line = estimatedLines.get(selection.planId);
                return {
                    ...selection,
                    noPayment: Boolean(
                        line
                        && Number(line.dueTodayAmount || 0) === 0
                        && Number(line.recurringAmount || 0) === 0
                    )
                };
            })
            : uuid
                ? [{ productId: this.category, planId: uuid, noPayment: false }]
                : [];
        if (queue.length === 0) {
            return { success: false, error: { message: "Choose at least one product plan." } };
        }

        this.writeProductOrderQueue(queue);
        const result = await this.processProductOrderQueue(queue);
        if (result?.success) this.onLicenseHideModal({});
        return result;
    }

    get hasProducts() {
        return (this.state.data || []).length > 0;
    }

    render() {
        return (
            <>
                <Col xs="12" className="products-dashboard">
                    <header className="products-dashboard-header">
                        <div>
                            <h1>My Products</h1>
                            <p>View and manage your licensed products and access.</p>
                        </div>
                        <Button className="new-license-button" onClick={this.onLicenseClick}>
                            <CsLineIcons icon="light-on" size="17" />
                            New License
                        </Button>
                    </header>
                </Col>
                {this.state.checkout.message ? (
                    <Col xs="12" className="products-dashboard">
                        <div className={`alert alert-${this.state.checkout.variant}`} role="status">
                            {this.state.checkout.message}
                        </div>
                    </Col>
                ) : null}
                {this.hasProducts && (
                    <Col xs="12" className="products-dashboard">
                        <div className="product-table-shell">
                        <Table
                            className="licensee-product-table"
                            headers={this.headers}
                            page={this.state.data}
                            sort={{ index: this.state.index, direction: this.state.direction }}
                            size={this.state.size}
                            index={this.state.page}
                            pages={this.state.pages}
                            onColumnClick={this.onColumnClick}
                            onPageChange={this.onPageChange}
                            onSearch={this.onSearch}
                            onSize={this.onSize}
                            searchable={this.state.pages > 1}
                            sizable={this.state.pages > 1}
                        />
                        </div>
                    </Col>
                )}
                {!this.hasProducts ? (
                    <Col xs="12" className="products-dashboard">
                        <div className="products-empty-state">
                            <span><CsLineIcons icon="grid-2" size="26" /></span>
                            <h2>No products yet</h2>
                            <p>Choose a Feradel product to get your workspace started.</p>
                            <Button onClick={this.onLicenseClick}>Explore Products</Button>
                        </div>
                    </Col>
                ) : null}
                <Col xs="12" className="products-dashboard">
                    <aside className="products-explore">
                        <span className="products-explore-icon"><CsLineIcons icon="light-on" size="25" /></span>
                        <span><strong>Looking for something else?</strong><small>Explore more products to grow your firm.</small></span>
                        <Button variant="outline-primary" onClick={this.onLicenseClick}>Explore Products <CsLineIcons icon="arrow-right" size="16" /></Button>
                    </aside>
                </Col>

                <LicenseModal
                    {...this.state.modal.license}
                    onCancel={this.onLicenseHideModal}
                    onOk={this.onLicenseOk}
                    category={this.category}
                    used={this.state.modifiers}
                    owned={this.state.data}
                />
            </>
        )
    }
}

Summary.propTypes = {
    auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
    return ({
        auth: state.auth,
    });
};

export default connect(mapStateToProps, {
    myProducts,
    getCodes,
    checkout,
    checkoutStatus
})(Summary);

